import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  convertPaymentsToTransactions,
  getPaymentDisplayAmount,
  getPaymentMethodText,
  getPaymentsTipAmount,
} from '../../helpers/payments';
import useGetSplitPaymentsGroup from '../../hooks/payments/useGetSpliPaymentsGroup';
import { formatDollarAmount } from '../../utils/global/helper';
import FullPageModal from '../modal/FullPageModal';
import TransactionBadge from '../transactions/TransactionBadge';
import Error from './SplitPaymentFlow/Error';
import CashTenderModal from '../form/CashTenderModal';

interface Props {
  splitPaymentsGroupId: string | null | undefined;
  onClose: () => void;
  onCashTenderClosed?: () => void;
}

const SPACING = 3;

function SplitPaymentsGroupOverview({
  splitPaymentsGroupId,
  onClose,
  onCashTenderClosed,
}: Props) {
  const [isCashTenderModalOpen, setIsCashTenderModalOpen] = useState(false);

  const theme = useTheme();

  const {
    data: splitPaymentsGroup,
    isLoading,
    isError,
  } = useGetSplitPaymentsGroup(splitPaymentsGroupId);

  const { payments = [] } = splitPaymentsGroup ?? {};

  const paymentsData = useMemo(
    () =>
      payments.map(payment => {
        // Customer-facing paid amount: under CD this is the inflated charge
        // (card) or the cash tender (feeless); non-CD is base + tax as before.
        const displayAmount = getPaymentDisplayAmount(payment);
        const tipAmount = getPaymentsTipAmount([payment]);
        const [transaction] = convertPaymentsToTransactions([payment]);
        return {
          id: payment.id,
          amount: displayAmount,
          tip: tipAmount,
          method: payment.method,
          transaction,
        };
      }),
    [payments],
  );

  const totalAmount = paymentsData.reduce(
    (acc, payment) => acc + payment.amount - payment.tip,
    0,
  );

  const totalCashAmount = useMemo(
    () =>
      paymentsData.reduce(
        (acc, payment) =>
          acc + (payment.method === 'CASH' ? payment.amount : 0),
        0,
      ),
    [paymentsData],
  );

  useEffect(() => {
    // Guard against stale data during a group switch: only act when the
    // fetched group actually matches the id we asked for.
    if (
      !splitPaymentsGroupId ||
      splitPaymentsGroup?.id !== splitPaymentsGroupId ||
      totalCashAmount <= 0
    ) {
      return;
    }
    setIsCashTenderModalOpen(true);
  }, [splitPaymentsGroupId, splitPaymentsGroup?.id, totalCashAmount]);

  return (
    <>
      <FullPageModal
        modalOpen={!!splitPaymentsGroupId}
        setModalOpen={onClose}
        name={splitPaymentsGroup ? formatDollarAmount(totalAmount) : ''}>
        {isLoading && (
          <Stack
            height="100%"
            justifyContent="center"
            alignItems="center"
            mt={4}>
            <CircularProgress />
          </Stack>
        )}
        {!isLoading && splitPaymentsGroup && (
          <Stack gap={SPACING} width="100%" maxWidth="sm" mx="auto" my={4}>
            <Stack
              border={`1px solid ${theme.palette.divider}`}
              borderRadius={1}
              padding={2.5}
              divider={<Divider sx={{ my: SPACING, borderWidth: '1px' }} />}>
              {paymentsData.map((payment, index) => (
                <Stack key={payment.id} gap={SPACING / 2}>
                  <TransactionBadge
                    transaction={payment.transaction}
                    sx={{
                      width: 'fit-content',
                    }}
                  />
                  <Typography variant="h3" component="p">
                    Payment {index + 1} -{' '}
                    {formatDollarAmount(payment.amount - payment.tip)}
                    {payment.tip > 0 &&
                      ` + ${formatDollarAmount(payment.tip)} tip`}
                  </Typography>
                  <Typography variant="subtitle2">
                    {getPaymentMethodText(payment.method)}
                  </Typography>
                  {payment.transaction.status === 'PENDING' && (
                    <Typography variant="caption" color="text.secondary">
                      If this payment was recently submitted, it may take a few
                      minutes to process.
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
            <Button variant="base" color="primary" onClick={onClose}>
              Close
            </Button>
            <Typography variant="caption" component="p" textAlign="center">
              You can view the full transaction details in the checkout page.
            </Typography>
          </Stack>
        )}
        {isError && (
          <Error message="Something went wrong while loading the payment data" />
        )}
      </FullPageModal>
      {isCashTenderModalOpen && (
        <CashTenderModal
          isSplitPayment
          open
          onClose={() => {
            setIsCashTenderModalOpen(false);
            onCashTenderClosed?.();
          }}
          saleTotalDollars={totalCashAmount}
          onDismissSuccess={() => {
            setIsCashTenderModalOpen(false);
            onCashTenderClosed?.();
          }}
        />
      )}
    </>
  );
}

export default SplitPaymentsGroupOverview;
